import React, { useState, useEffect, useContext } from "react";
import { Redirect } from "react-router-dom";
import { UncontrolledAlert, Row, Col } from "reactstrap";
import { faCreditCard } from "@fortawesome/free-solid-svg-icons";
import {
  faCcAmex,
  faCcVisa,
  faCcMastercard,
  faCcDiscover
} from "@fortawesome/free-brands-svg-icons";
// contexts
import { StoreContext } from "components/StoreContext";
import { CartContext } from "components/CartContext";
// helper files and components
import { debug, states } from "helpers";
import Loading from "components/Loading";
import CardAnimationLoading from "components/Loading/cardAnimationLoading";
import HeadTags from "components/HeadTags";
// billing section
import BillingHeader from "./components/billing/billingHeader";
import BillingStepAddress from "./components/billing/billingStepAddress";
import BillingStepAddressCompressed from "./components/billing/billingStepAddressCompressed";
// card section
import CardHeader from "./components/card/cardHeader";
import CardStep from "./components/card/cardStep";
import CardStepCompressed from "./components/card/cardStepCompressed";
import CardStepTotalZero from "./components/card/cardStepTotalZero";
// review
import CheckoutReview from "./components/checkoutReview";
import CheckoutConfirmed from "./components/checkoutConfirmed";
import OrderSummary from "./components/orderSummary";

import { pushPlaceOrderEventToGTM } from "./helpers/helpersCheckout";

const Checkout = () => {
  const { config, store, StoreAPI } = useContext(StoreContext);
  const {
    loading: customerLoading,
    customer,
    customerAddress,
    updateCustomerAddress,
    createCustomerAddress,
    cart,
    onCartRemove,
    calculateCart,
    clearCart,
    error: cartError
  } = useContext(CartContext);
  const {
    items,
    subtotal,
    totalDiscount,
    promotionalDiscount,
    salesTax,
    total
  } = cart;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [billing, setBilling] = useState({
    displayName: customerAddress.displayName,
    address: customerAddress.address1,
    city: customerAddress.cityName,
    stateName: customerAddress.stateName,
    stateCode: states[customerAddress.stateName],
    countryName: customerAddress.countryName,
    zip: customerAddress.postalCode,
    phone: customer.phone
  });

  const [card, setCard] = useState({
    type: "",
    number: "",
    expiration: "",
    mmExpiration: "",
    yyExpiration: "",
    security: "",
    name: ""
  });

  const [cardHelp, setCardHelp] = useState({
    type: null,
    icon: faCreditCard,
    help: null,
    digits: 3
  });

  const [steps, setSteps] = useState({
    billingVisible: true,
    billingValid: false,
    billingComplete: false,
    billingEditable: false,
    cardVisible: false,
    cardValid: false,
    cardComplete: false,
    cardEditable: false,
    reviewVisible: false,
    reviewError: false,
    reviewComplete: false
  });

  const [valid, setValid] = useState({
    displayName: true,
    countryName: true,
    address: true,
    city: true,
    stateName: true,
    stateCode: true,
    zip: true,
    phone: true,
    number: true,
    expiration: true,
    mmExpiration: true,
    yyExpiration: true,
    security: true,
    name: true,
    accept: false
  });

  const [order, setOrder] = useState({
    success: false,
    email: null,
    id: null,
    items: [],
    subtotal: 0,
    total: 0,
    totalDiscount: null,
    salesTax: null,
    promotionalDiscount: null
  });

  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();

  const handleCCFormatting = e => {
    const numbers = e.target.value.match(/\d+/g);
    const formatted =
      numbers !== null ? numbers.join("").slice(0, 19) : numbers;

    setCard({
      ...card,
      ...{
        number: formatted || ""
      }
    });
  };

  const handleCardHelp = () => {
    const cvchelpBack = "A 3-digit number on the back of your credit card";
    const cvchelpFront = "A 4-digit number above your credit card number";
    const id = card.number ? card.number.slice(0, 1) : null;
    switch (id) {
      case "3":
        setCardHelp({
          type: "amex",
          icon: faCcAmex,
          help: cvchelpFront,
          digits: 4
        });
        break;
      case "4":
        setCardHelp({
          type: "visa",
          icon: faCcVisa,
          help: cvchelpBack,
          digits: 3
        });
        break;
      case "5":
        setCardHelp({
          type: "mastercard",
          icon: faCcMastercard,
          help: cvchelpBack,
          digits: 3
        });
        break;
      case "6":
        setCardHelp({
          type: "discover",
          icon: faCcDiscover,
          help: cvchelpBack,
          digits: 3
        });
        break;
      default:
        setCardHelp({
          type: "",
          icon: faCreditCard,
          help: "",
          digits: 3
        });
    }
    if (card.type !== cardHelp.type) {
      setCard({
        ...card,
        ...{ type: cardHelp.type }
      });
    }
  };

  const handleStepBilling = () => {
    setError(null);
    let errors = 0;
    const validated = {
      displayName: true,
      address: true,
      countryName: true,
      city: true,
      stateName: true,
      zip: true
    };

    const postalCodeRegex = new RegExp(
      /(^\d{5}(-\d{4})?$)|(^[ABCEGHJKLMNPRSTVXY]{1}\d{1}[A-Z]{1} *\d{1}[A-Z]{1}\d{1}$)/
    );

    if (billing.displayName === null || billing.displayName === "") {
      errors += 1;
      validated.displayName = false;
    }
    if (billing.address === null || billing.address === "") {
      errors += 1;
      validated.address = false;
    }
    if (billing.countryName === null || billing.countryName === "") {
      errors += 1;
      validated.countryName = false;
    }
    if (billing.city === null || billing.city === "") {
      errors += 1;
      validated.city = false;
    }
    if (billing.stateName === null || billing.stateName === "Choose") {
      errors += 1;
      validated.stateName = false;
    }
    if (
      billing.zip === null ||
      postalCodeRegex.test(billing.zip) === false ||
      billing.zip === ""
    ) {
      errors += 1;
      validated.zip = false;
    }

    // skip billing if total = 0
    if (total === 0) {
      validated.number = true;
      validated.expiration = true;
      validated.security = true;
      validated.name = true;
    }

    setValid({
      ...valid,
      ...validated
    });

    if (errors === 0) {
      // skip billing if total = 0
      if (total === 0) {
        setSteps({
          ...steps,
          ...{
            billingVisible: true,
            billingComplete: true,
            billingEditable: true,
            cardVisible: true,
            cardComplete: true,
            cardEditable: false,
            reviewVisible: true
          }
        });
      } else {
        setSteps({
          ...steps,
          ...{
            billingVisible: true,
            billingComplete: true,
            billingEditable: true,
            cardVisible: true,
            cardEditable: steps.cardComplete,
            reviewVisible: steps.cardComplete
          }
        });
      }

      if (customerAddress.addressId) {
        updateCustomerAddress(
          customerAddress.customerId,
          customerAddress.addressId,
          billing
        );
      } else {
        createCustomerAddress(customer.znodeUserId, billing);
      }
      calculateCart({
        billing,
        promosToApply: cart.appliedPromo
      });
    }
  };

  const handleStepCard = () => {
    setError(null);
    let errors = 0;
    const validated = {
      number: true,
      expiration: true,
      mmExpiration: true,
      yyExpiration: true,
      security: true,
      name: true
    };

    if (card.number.length < 13 || card.number.length > 19) {
      errors += 1;
      validated.number = false;
    }

    if (card.expiration.length !== 5) {
      errors += 1;
      validated.expiration = false;
    }

    if (card.security.length < 3) {
      errors += 1;
      validated.security = false;
    }

    if (card.name.length < 1) {
      errors += 1;
      validated.name = false;
    }

    // Hannah Neal: If user clicks "save and continue" and has not entered anything into the form, an error will be triggered.
    if (card.expiration.substring(0, 2) === "MM") {
      errors += 1;
      validated.expiration = false;
    }
    if (card.expiration.substring(3, 5) === "YY") {
      errors += 1;
      validated.expiration = false;
    }

    setValid({
      ...valid,
      ...validated
    });

    if (errors === 0) {
      setSteps({
        ...steps,
        ...{
          billingVisible: true,
          billingEditable: steps.billingComplete,
          cardVisible: true,
          cardComplete: true,
          cardEditable: true,
          reviewVisible: true
        }
      });
    }
  };

  const placeOrder = () => {
    const data = {
      HStreamId: customer.hStreamId,
      Cart: {
        CartItems: items.map(item => {
          const product = {
            description: item.productName,
            quantity: 1,
            sku: item.sku,
            unitPrice: item.unitPrice,
            productName: item.productName
          };
          if (item.unitPrice === 0) {
            product.isFreeProduct = true;
          }
          return product;
        }),
        subTotal: subtotal,
        salesTax: cart.salesTax,
        total,
        customerId: customer.znodeUserId,
        cookieMappingId: cart.cookie,
        PromotionCouponCode: cart.appliedPromo
      },
      BillingInformation: {
        AddressId: customerAddress.addressId,
        FirstName: customer.firstName,
        LastName: customer.lastName,
        Email: customer.emailAddress,
        BillingName: billing.displayName,
        StreetAddress: billing.address,
        City: billing.city,
        StateName: billing.stateName,
        StateCode: billing.stateCode,
        PostalCode: billing.zip,
        CountryName: billing.countryName,
        PhoneNumber: customer.phoneNumber,
        PaymentSettingId: store.paymentSettingId,
        PaymentType: "CREDIT_CARD"
      }
    };

    if (total > 0) {
      data.BillingInformation.CardType = card.type.toUpperCase();
      data.BillingInformation.CardNumber = card.number;
      data.BillingInformation.CardHolderName = card.name;
      data.BillingInformation.CardExpirationMonthYear = card.expiration;
      data.BillingInformation.CardSecurityCode = card.security;
    }

    setLoading(true);
    setError(null);

    StoreAPI.request({
      headers: {
        ...StoreAPI.defaults.headers,
        ...{ "hstm-config-name": `${config.envName}|${config.appId}` }
      },
      method: "post",
      url: "orders",
      data
    })
      .then(response => {
        debug(response);

        const oldItem = response.data.orderLineItems;

        // Hannah Neal: This puts the products, or "items" object into the format desired for Admin to view in Google Analytics:
        const newItemsCollection = oldItem.map(i => {
          const newItem = {
            sku: i.sku,
            name: i.productName,
            price: i.unitPrice,
            quantity: i.quantity
          };

          return newItem;
        });

        pushPlaceOrderEventToGTM(response.data, newItemsCollection);

        setOrder({
          success: true,
          id: response.data.orderNumber,
          email: customer.emailAddress,
          items: response.data.orderLineItems,
          subtotal: response.data.subTotal,
          total: response.data.total,
          salesTax: response.data.taxCost,
          totalDiscount: response.data.discount,
          promotionalDiscount: response.data.promotionalDiscount
        });
        clearCart();
      })
      .catch(err => {
        let message =
          "There was a problem processing your order. Please try again.";
        if (
          err.request.status &&
          err.request.status === 422 &&
          err.request.responseText
        ) {
          const response = JSON.parse(err.request.responseText);
          message = response.detail;
        } else if (
          err.request.status &&
          err.request.status >= 500 &&
          err.request.status <= 599
        ) {
          message =
            "We're sorry, there was a problem processing your order. Please try again later.";
        }

        setOrder(e => ({
          ...e,
          success: false
        }));

        setValid({
          ...valid,
          ...{
            accept: false
          }
        });

        if (total === 0) {
          setSteps({
            ...steps,
            ...{
              billingVisible: true,
              billingComplete: false,
              billingEditable: false,
              cardVisible: false,
              cardComplete: false,
              cardEditable: false,
              reviewVisible: false
            }
          });
        } else {
          setSteps({
            ...steps,
            ...{
              billingVisible: false,
              billingComplete: true,
              billingEditable: true,
              cardVisible: true,
              cardComplete: false,
              cardEditable: false,
              reviewVisible: false
            }
          });
        }
        setError(message);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleStepOrder = () => {
    if (!valid.review) {
      setSteps({
        ...steps,
        ...{
          reviewError: true
        }
      });
    }

    if (Object.keys(valid).every(field => valid[field])) {
      placeOrder();
    }
  };

  const handleBillingHeaderEdit = () => {
    if (!steps.cardEditable) {
      setSteps({
        ...steps,
        ...{
          billingVisible: true,
          billingComplete: false,
          billingEditable: false,
          cardVisible: true,
          cardEditable: false,
          reviewVisible: false
        }
      });
    }

    if (steps.cardEditable) {
      setSteps({
        ...steps,
        ...{
          billingVisible: true,
          billingComplete: false,
          billingEditable: false,
          cardVisible: true,
          cardEditable: true,
          reviewVisible: false
        }
      });
    }

    setValid({
      ...valid,
      ...{
        accept: false
      }
    });
  };

  const handleCardHeaderEdit = () => {
    if (!steps.billingEditable) {
      setSteps({
        ...steps,
        ...{
          billingVisible: true,
          billingEditable: false,
          cardVisible: true,
          cardComplete: false,
          cardEditable: false,
          reviewVisible: false
        }
      });
    }

    if (steps.billingEditable) {
      setSteps({
        ...steps,
        ...{
          billingVisible: true,
          billingEditable: true,
          cardVisible: true,
          cardComplete: false,
          cardEditable: false,
          reviewVisible: false
        }
      });
    }

    setValid({
      ...valid,
      ...{
        accept: false
      }
    });
  };

  const handleCardSecurity = e => {
    setCard({
      ...card,
      ...{ security: e.target.value.replace(/\D/g, "") }
    });
  };

  const handleCardName = e => {
    setCard({
      ...card,
      ...{ name: e.target.value }
    });
  };

  const handleOrderSummaryValid = () => {
    setValid({
      ...valid,
      ...{
        accept: !valid.accept
      }
    });
  };

  useEffect(() => {
    handleCardHelp();
    if (cartError) {
      setError(cartError);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerAddress.addressId, card, cart, cartError, loading]);

  useEffect(() => {
    if (customerAddress.addressId) {
      setBilling({
        displayName: customerAddress.displayName,
        address: customerAddress.address1,
        countryName: customerAddress.countryName,
        city: customerAddress.cityName,
        stateName: customerAddress.stateName,
        stateCode: states[customerAddress.stateName],
        zip: customerAddress.postalCode,
        phone: customer.phone
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerAddress.addressId]);

  // exit to cart if no logged in user or empty cart
  if (!order.success && cart.items.length < 1) {
    return <Redirect to="/cart" />;
  }
  return (
    <>
      <HeadTags title="Checkout" />
      <section className="page page--checkout" role="main">
        <div className="container">
          <div className="page__body">
            <div className="checkout">
              <div className="checkout__container">
                <Row>
                  <Col md="7">
                    {error && (
                      <UncontrolledAlert color="danger" className="mb-4">
                        {error}
                      </UncontrolledAlert>
                    )}

                    {!order.success && loading && <CardAnimationLoading />}

                    {!order.success && !loading && (
                      <div>
                        <div className="checkout__step">
                          <BillingHeader
                            billingComplete={steps.billingComplete}
                            billingEditable={steps.billingEditable}
                            handleBillingHeaderEdit={handleBillingHeaderEdit}
                          />
                          {steps.billingVisible && !steps.billingComplete && (
                            <BillingStepAddress
                              googleApiKey={config.googleApiKey}
                              billing={billing}
                              setBilling={setBilling}
                              valid={valid}
                              handleStepBilling={handleStepBilling}
                              allowCanadianPayments={
                                config.features.EnableCanadaCustomers
                              }
                            />
                          )}
                          {steps.billingVisible && steps.billingComplete && (
                            <div className="checkout__step-body">
                              <BillingStepAddressCompressed
                                displayName={billing.displayName}
                                address={billing.address}
                                city={billing.city}
                                stateName={billing.stateCode}
                                zip={billing.zip}
                                countryName={billing.countryName}
                              />
                            </div>
                          )}
                        </div>
                        <div className="checkout__step">
                          <CardHeader
                            cardComplete={steps.cardComplete}
                            cardEditable={steps.cardEditable}
                            handleCardHeaderEdit={handleCardHeaderEdit}
                          />
                          {total > 0 &&
                            steps.cardVisible &&
                            !steps.cardComplete && (
                              <CardStep
                                validNumber={valid.number}
                                cardHelpIcon={cardHelp.icon}
                                cardNumber={card.number}
                                handleCCFormatting={handleCCFormatting}
                                card={card}
                                setCard={setCard}
                                cardHelpDigits={cardHelp.digits}
                                cardSecurity={card.security}
                                validSecurity={valid.security}
                                cardHelpHelp={cardHelp.help}
                                cardName={card.name}
                                validName={valid.name}
                                valid={valid}
                                mmExpiration={valid.mmExpiration}
                                yyExpiration={valid.yyExpiration}
                                expiration={valid.expiration}
                                currentYear={currentYear}
                                handleCardSecurity={handleCardSecurity}
                                handleCardName={handleCardName}
                                handleStepCard={handleStepCard}
                              />
                            )}
                          {total > 0 &&
                            steps.cardVisible &&
                            steps.cardComplete && (
                              <CardStepCompressed
                                cardHelpIcon={cardHelp.icon}
                                cardNumber={card.number}
                                cardExpiration={card.expiration}
                                cardSecurity={card.security}
                                cardName={card.name}
                              />
                            )}
                          {total === 0 && steps.cardVisible && (
                            <CardStepTotalZero />
                          )}
                        </div>
                        <CheckoutReview
                          stepsReviewVisible={steps.reviewVisible}
                          items={items}
                          onCartRemove={onCartRemove}
                        />
                      </div>
                    )}

                    {order.success && (
                      <CheckoutConfirmed
                        orderID={order.id}
                        orderEmail={order.email}
                      />
                    )}
                  </Col>
                  <Col md="5">
                    <OrderSummary
                      orderSuccess={order.success}
                      items={items}
                      totalDiscount={totalDiscount}
                      promotionalDiscount={promotionalDiscount}
                      subtotal={subtotal}
                      salesTax={salesTax}
                      billingStateCode={billing.stateCode}
                      total={total}
                      stepsReviewVisible={steps.reviewVisible}
                      validAccept={valid.accept}
                      handleOrderSummaryValid={handleOrderSummaryValid}
                      handleStepOrder={handleStepOrder}
                      loading={loading}
                      orderItems={order.items}
                      orderTotalDiscount={order.totalDiscount}
                      orderSubtotal={order.subtotal}
                      orderPromotionalDiscount={order.promotionalDiscount}
                      orderSalesTax={order.salesTax}
                      orderTotal={order.total}
                    />
                  </Col>
                </Row>
                {customerLoading && <Loading blocking />}
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Checkout;
